import { Section } from '@all-hands-hq/plex-graphql-schema';
import { Card, CircularProgress, createStyles, makeStyles, Theme } from '@material-ui/core';
import React from 'react';

import { GetDocumentToImportQuery } from '../../../../graphql/gen/types';
import { spacings } from '../../../../theme';
import { notEmpty } from '../../../../utils/notEmpty';
import { DocumentToImportAction, DocumentToImportState } from './documentToImportReducer';
import { GetDocumentToImportErrorView } from './GetDocumentToImportErrorView';
import { GoogleOutlinePreview } from './GoogleDocPreview';

export const BoardGoogleDocPreview = ({
  data,
  loading,
  documentToImport,
  dispatch,
}: {
  data: GetDocumentToImportQuery | undefined;
  loading: boolean;
  documentToImport: DocumentToImportState | undefined;
  dispatch: React.Dispatch<DocumentToImportAction>;
}): React.ReactElement | null => {
  const classes = useStyles();

  if (loading || data != null) {
    return (
      <Card className={classes.documentPreview}>
        {loading && <CircularProgress />}
        {data != null && <GetDocumentToImportErrorView data={data} />}

        {documentToImport != null && (
          <GoogleOutlinePreview
            document={documentToImport.document}
            selectedSections={[...documentToImport.selectedSections]}
            faqBoardSections={
              new Set(
                [
                  getFaqBoardSection({
                    selectedSections: documentToImport.selectedSections,
                  }),
                ].filter(notEmpty)
              )
            }
            faqSections={getFaqSections({
              selectedSections: documentToImport.selectedSections,
            })}
            onSelectionRowClick={(section: Section): void => dispatch({ type: 'ROW_CLICK', section })}
            onSelectionCheckboxClick={(section: Section): void => dispatch({ type: 'CHECKBOX_CLICK', section })}
            onUpdateSelection={(selectedSections: Section[]): void => {
              dispatch({ type: 'UPDATE_SELECTION', selectedSections });
            }}
          />
        )}
      </Card>
    );
  } else {
    return null;
  }
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    documentPreview: {
      marginTop: spacings.normal,
      padding: spacings.normal,
      textAlign: 'center',
    },
  })
);

/*
We will select the nodes which are directly under selected sections which are FAQ Board sections,
which have at least some content selected.
*/
export function getFaqSections({ selectedSections }: { selectedSections: Set<Section> }): Set<Section> {
  const boardSection = getFaqBoardSection({ selectedSections });
  const faqBoardChildrenSections = new Set(
    (boardSection != null ? [boardSection] : []).map((section) => section.children).flat()
  );

  const result: Set<Section> = new Set();

  for (const selectedSection of selectedSections) {
    if (faqBoardChildrenSections.has(selectedSection)) {
      result.add(selectedSection);
    }
  }

  return result;
}

/*
We will select the least common ancestor as the section which will name the PlexBase Board.
*/
export function getFaqBoardSection({ selectedSections }: { selectedSections: Set<Section> }): Section | null {
  let lowestCommonAncestor: Section | null = null;
  for (const selectedSection of selectedSections) {
    if (lowestCommonAncestor == null) {
      lowestCommonAncestor = selectedSection;
    } else {
      lowestCommonAncestor = Section.getLowestCommonAncestor(lowestCommonAncestor, selectedSection);
    }
  }

  return lowestCommonAncestor;
}
